import { Icon } from "@iconify/react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import ListSubheader from "@mui/material/ListSubheader";
import Modal from "@mui/material/Modal";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Skeleton from "@mui/material/Skeleton";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckboxSelect, JobStatusDropdown, StepperLabelIcon } from "../../components";
import EmployeeListDropdown from "../../components/analyst/EmployeeListDropdown";
import {
  ButtonAsDropdown,
  renderCTAButton,
  renderCTAButtonWithIcon,
  renderDeleteButton,
  renderEditButton,
  renderFileInputButton,
  renderGhostButton,
  renderGhostButtonWithIcon,
  renderOutlineButton,
  renderPreviewButtonWithIcon,
  renderToggleButton,
} from "../../components/buttons";
import { renderInput, renderTextArea, Select as SelectInput } from "../../components/form-components";
import { Input } from "../../components/form-components/inputs";
import { AnalystJobViewProvider, useAnalystJobView } from "../../context";
import { Departments, financialDocumentsTitles, jobDocumentsTitles, receptionistName } from "../../lib/constants";
import { capitalizeString, formatAmount, formatDate } from "../../lib/utils";
import { useJob } from "../../services";

const ModalWrapper = styled(Modal)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const renderLabelValueRow = (label, value) => (
  <div className="row small align_start">
    <span className="label gray">{label}</span>
    <span className="value text_right">{value}</span>
  </div>
);

const renderSectionTitle = (title) => <div className="font-md text-md">{title}</div>;

const LoadingState = ({ height = 40 }) => <Skeleton variant="rounded" width="100%" height={height} />;

const renderTaskName = (taskName) => {
  if (!taskName) throw new Error("taskName is required");

  return (
    <div>
      <span>Work: </span>
      <span>
        {`${taskName}`.charAt(0).toUpperCase()}
        <span className="xs">{`${taskName}`.slice(1)}</span>
      </span>
    </div>
  );
};

const formatTimeString = (timeString) => {
  const [hours, minutes] = timeString.split(":");
  return `${hours}hrs:${minutes}mins`;
};

const getTimeUsedClass = (allotedTime, timeUsed) => {
  const allotedTimeArr = allotedTime.split(":");
  const timeUsedArr = timeUsed.split(":");

  const allotedTimeInMinutes = parseInt(allotedTimeArr[0] ?? 0) * 60 + parseInt(allotedTimeArr[1] ?? 0);
  const timeUsedInMinutes = parseInt(timeUsedArr[0] ?? 0) * 60 + parseInt(timeUsedArr[1] ?? 0);

  return allotedTimeInMinutes === timeUsedInMinutes
    ? ""
    : allotedTimeInMinutes > timeUsedInMinutes
    ? "text-green"
    : "text-red";
};

const renderProjectDetailsForTask = (jobCode, jobName, employee) => (
  <div className="grid_3 grid-with-divider">
    <div className="row">
      <span>Job code</span>
      <span className="font-md">{jobCode}</span>
    </div>
    <div className="row">
      <span>Project Name</span>
      <span className="font-md">{jobName}</span>
    </div>
    <div className="row">
      <span>Employee</span>
      <span>{employee}</span>
    </div>
  </div>
);

const CommonJobDetails = () => {
  const { loadStatus, commonJobDetails, handleAssignNewSiteVisitClick } = useAnalystJobView();
  const { jobCode, jobName, jobStatus, jobValue, milestoneCount, clientName, milestonesCompleted, jobDate } =
    commonJobDetails;

  return loadStatus.loading ? (
    <LoadingState />
  ) : (
    <div className="card rounded column">
      <div className="row">
        <span>
          <span className="font-md">Job Code: </span>
          {jobCode}
        </span>
        <span>{formatDate(jobDate)}</span>
        <span className="font-md">{jobName}</span>
        <JobStatusDropdown status={jobStatus} />
        {/* <span className="font-md">{jobStatus}</span> */}
      </div>
      <div className="row">
        <span>
          <span className="font-md">Client Name: </span>
          {clientName}
        </span>
        <span>
          <span className="font-md">Job Value: </span>
          {formatAmount(jobValue)}
        </span>
        <span>
          <span className="font-md">Milestones: </span>
          {milestonesCompleted}/{milestoneCount}
        </span>
        {renderOutlineButton("+ Schedule Site Visit", true, "button", { onClick: handleAssignNewSiteVisitClick })}
      </div>
    </div>
  );
};

const JobDetails = ({ hide = false }) => {
  const { basicDetails, typeOfWork, poc, files } = useAnalystJobView();

  const { siteAddress, locality } = basicDetails;
  const { isAlliedWork, isBuildingWork, jobType, ...work } = typeOfWork;
  const { client, architect, builder, owner, sanctioningAuthority } = poc;
  const { inwardDocuments, outwardDocuments, financialDocuments } = files;

  const [isSectionVisible, setIsSectionVisible] = useState(true);

  useEffect(() => {
    setIsSectionVisible(!hide);
  }, [hide]);

  const renderBasicDetails = () => (
    <section className="card rounded column small">
      {renderSectionTitle("Basic Details")}
      <div className="grid-2-cols-with-divider">
        {renderLabelValueRow("Site Address", siteAddress)}
        {renderLabelValueRow("Locality", locality)}
        {/* <div className="column">
          {renderLabelValueRow("Name of the Project", jobName)}
          {renderLabelValueRow("Project Date", formatDate(jobDate))}
        </div>
        <div className="column">
          {renderLabelValueRow("Site Address", siteAddress)}
          {renderLabelValueRow("Locality", locality)}
        </div> */}
      </div>
    </section>
  );

  const renderAlliedWorkDetails = () =>
    isAlliedWork ? (
      <div className="grid-2-cols-with-divider small">
        <div className="column">
          {renderLabelValueRow("Work type", jobType)}
          {renderLabelValueRow("Allied Work", work.alliedWorkSpecification)}
        </div>
        <div className="column">
          <div className="label">No. of structural drawings:</div>
          <div className="row">
            {renderLabelValueRow("Column footing drawing:", work.columnDrawings)}
            {renderLabelValueRow("No. of slabs:", work.slabsCount)}
          </div>
        </div>
      </div>
    ) : null;

  const renderBuildingWorkDetails = () =>
    isBuildingWork ? (
      <div className="grid-2-cols-with-divider small">
        <div className="column">
          {renderLabelValueRow("Work type", jobType)}
          {renderLabelValueRow("Building Work", work.buildingType)}
          {renderLabelValueRow("Basement", work.basementCount)}
          {renderLabelValueRow("Parking", work.parkingCount)}
          {renderLabelValueRow("Ground Floor", work.groundFloor)}
        </div>
        <div className="column">
          {renderLabelValueRow("No. of upper floors", work.upperFloorCount)}
          {work.isExistingWork
            ? renderLabelValueRow("No. of existing floors", work.existingFloorCount)
            : renderLabelValueRow("Provision for additional floors", work.hasAdditionalFloor ? "Yes" : "No")}
          <div className="column end">
            <div className="label">No. of structural drawings:</div>
            <div className="row">
              {work.isExistingWork ? null : renderLabelValueRow("Column footing drawing:", work.columnDrawings)}
              {renderLabelValueRow("No. of slabs:", work.slabsCount)}
            </div>
          </div>
        </div>
      </div>
    ) : null;

  const renderTypeofWorkDetails = () => (
    <div className="card rounded column">
      {renderSectionTitle("Type of Work")}
      {renderAlliedWorkDetails()}
      {renderBuildingWorkDetails()}
    </div>
  );

  const renderPOCDetails = () => (
    <div className="card rounded column spread">
      {renderSectionTitle("Point of Communication")}
      <div className="grid-2-cols-with-divider small">
        <div className="column">
          {renderSectionTitle("1. Client Details")}
          {renderLabelValueRow("Full Name", client?.name)}
          {renderLabelValueRow("Phone Number", client?.phone)}
          {renderLabelValueRow("Mobile Number", client?.mobile)}
          {renderLabelValueRow("Whatsapp Number", client?.whatsapp)}
          {renderLabelValueRow("Email Address", client?.email)}
          {renderLabelValueRow("Company Name", client?.company_name)}
          {renderLabelValueRow("Office Address", client?.office_address)}
          {renderLabelValueRow("GST Number", client?.gst_number)}
          {renderLabelValueRow("Work Order", client?.work_order)}
        </div>
        <div className="column">
          {renderSectionTitle("2. Architect Details")}
          {renderLabelValueRow("Full Name", architect?.name)}
          {renderLabelValueRow("Phone Number", architect?.phone)}
          {renderLabelValueRow("Mobile Number", architect?.mobile)}
          {renderLabelValueRow("Whatsapp Number", architect?.whatsapp)}
          {renderLabelValueRow("Email Address", architect?.email)}
          {renderLabelValueRow("Office Address", architect?.office_address)}
        </div>
      </div>
      <div className="grid-2-cols-with-divider small">
        <div className="column">
          {renderSectionTitle("3. Builder Details")}
          {renderLabelValueRow("Full Name", builder?.name)}
          {renderLabelValueRow("Phone Number", builder?.phone)}
          {renderLabelValueRow("Mobile Number", builder?.mobile)}
          {renderLabelValueRow("Whatsapp Number", builder?.whatsapp)}
          {renderLabelValueRow("Email Address", builder?.email)}
          {renderLabelValueRow("Office Address", builder?.office_address)}
        </div>
        <div className="column">
          {renderSectionTitle("4. Owner Details")}
          {renderLabelValueRow("Full Name", owner?.name)}
          {renderLabelValueRow("Phone Number", owner?.phone)}
          {renderLabelValueRow("Mobile Number", owner?.mobile)}
          {renderLabelValueRow("Whatsapp Number", owner?.whatsapp)}
          {renderLabelValueRow("Email Address", owner?.email)}
          {renderLabelValueRow("Locality", locality)}
          {renderLabelValueRow("Site Address", siteAddress)}
          {renderLabelValueRow("Building Name", owner?.building_name)}
          {renderLabelValueRow("Name of Supervisor", owner?.supervisor_name)}
          {renderLabelValueRow("Contact No. of Supervisor", owner?.supervisor_contact)}
          {renderLabelValueRow("Sanction No.", owner?.sanction_number ?? "-")}
          {renderLabelValueRow("Sanction Date", owner?.sanction_date ? formatDate(owner.sanction_date) : "-")}
        </div>
      </div>
      <div className="row small">
        {renderSectionTitle("5. Sanctioning Authority")}
        <span>{sanctioningAuthority}</span>
      </div>
    </div>
  );

  const renderFilesSection = () => (
    <div className="card rounded column">
      {renderSectionTitle("Files Section")}
      <div className="grid-2-cols-with-divider align_start">
        <div className="column spread">
          <div className="column">
            <div className="row">
              <span>Inward Documents</span>
            </div>
            <ol className="column">
              {inwardDocuments.length > 0 ? (
                inwardDocuments.map((doc) => (
                  <li key={doc}>
                    <div className="row">
                      <span>{doc.name}</span>
                      {renderGhostButton("Preview", false, "link", { to: doc.url, target: "_blank" })}
                    </div>
                  </li>
                ))
              ) : (
                <span className="small gray">No inward documents uploaded</span>
              )}
            </ol>
          </div>
          <div className="column">
            <div className="row">
              <span>Financial Documents</span>
            </div>
            <ol className="column">
              {financialDocuments.length > 0 ? (
                financialDocuments.map((doc) => (
                  <li key={doc}>
                    <div className="row">
                      <span>{doc.name}</span>
                      {renderPreviewButtonWithIcon("Preview", "link", { to: doc.url, target: "_blank" })}
                    </div>
                  </li>
                ))
              ) : (
                <span className="small gray">No financial documents available</span>
              )}
            </ol>
          </div>
        </div>
        <div className="column">
          <div className="row">
            <span>Outward Documents</span>
          </div>
          <ol className="column">
            {outwardDocuments.length > 0 ? (
              outwardDocuments.map((doc) => (
                <li key={doc}>
                  <div className="row">
                    <span>{doc.name}</span>
                    {renderPreviewButtonWithIcon("Preview", "link", { to: doc.url, target: "_blank" })}
                  </div>
                </li>
              ))
            ) : (
              <span className="small gray">No outward documents uploaded</span>
            )}
          </ol>
        </div>
      </div>
    </div>
  );

  const renderHiddenDetails = () => {
    if (!isSectionVisible) return null;
    return (
      <section className="column">
        {renderBasicDetails()}
        {renderTypeofWorkDetails()}
        {renderPOCDetails()}
        {renderFilesSection()}
      </section>
    );
  };

  return (
    <div className="column card">
      <div className="row">
        {renderSectionTitle("Project Summary")}
        {renderToggleButton(null, isSectionVisible, () => setIsSectionVisible((prev) => !prev))}
      </div>
      {renderHiddenDetails()}
    </div>
  );
};

const JobDetailsView = () => {
  const { addJobSteps } = useAnalystJobView();

  const renderStepper = () => (
    <Stepper alternativeLabel nonLinear>
      {addJobSteps.map((step) => (
        <Step key={step.label} active={step.status === "active"} completed={step.status === "completed"}>
          <StepButton
            icon={<StepperLabelIcon active={step.status === "active"} completed={step.status === "completed"} />}
          >
            {step.label}
          </StepButton>
        </Step>
      ))}
    </Stepper>
  );

  return <>{renderStepper()}</>;
};

const MilestoneView = () => {
  const { milestones, milestonesCompleted, updateMilestoneStatus } = useAnalystJobView();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMilestone, setActiveMilestone] = useState(null);
  const [milestoneToggles, setMilestoneToggles] = useState({});
  const { id: jobId } = useParams();
  const { markMilestone } = useJob();

  useEffect(() => {
    const milestonesData = {};
    for (const milestone of milestones) {
      milestonesData[milestone.milestone_number] = milestone;
    }
    setMilestoneToggles(milestonesData);
  }, [milestones]);

  // Handle step button click
  const handleStepClick = (event, milestone) => {
    const newAnchorEl = anchorEl === event.currentTarget ? null : event.currentTarget;
    setAnchorEl(newAnchorEl);
    setActiveMilestone(milestone);
  };

  // Handle toggle switch change
  const handleToggleChange = async (milestone) => {
    const updatedMilestone = await updateMilestoneStatus(milestone);
    setMilestoneToggles({
      ...milestoneToggles,
      [milestone.milestone_number]: updatedMilestone,
    });
  };

  const renderMilestoneStepper = () => (
    <Stepper alternativeLabel nonLinear>
      {milestones.map((step, index) => (
        <Step
          key={step.milestone_number}
          active={index === milestonesCompleted}
          completed={index < milestonesCompleted}
          sx={{ position: "relative" }}
        >
          <StepButton
            icon={<StepperLabelIcon active={index === milestonesCompleted} completed={index < milestonesCompleted} />}
            disabled={index > milestonesCompleted || !["not_started", "milestone_achieved"].includes(step.status)}
            onClick={(e) => {
              if (!["not_started", "milestone_achieved"].includes(step.status)) return;
              if (index > milestonesCompleted) return;
              handleStepClick(e, step);
            }}
          >
            {step.milestone}
          </StepButton>
          <Popper open={open} anchorEl={anchorEl} placement="bottom" disablePortal style={{ zIndex: 1300 }}>
            <PopperContainer>
              <span className="font-md">Milestone achieved</span>
              <Switch
                checked={
                  activeMilestone
                    ? milestoneToggles[activeMilestone.milestone_number]?.status === "milestone_achieved" || false
                    : false
                }
                onChange={(e) => {
                  if (!activeMilestone) return;
                  handleToggleChange(activeMilestone);
                }}
                disabled={
                  !activeMilestone ||
                  !["not_started", "milestone_achieved"].includes(
                    milestoneToggles[activeMilestone.milestone_number]?.status
                  )
                }
                color={activeMilestone && milestoneToggles[activeMilestone.milestone_number] ? "success" : "default"}
              />
            </PopperContainer>
          </Popper>
        </Step>
      ))}
    </Stepper>
  );

  const open = Boolean(anchorEl);

  return (
    <div className="card column">
      {renderSectionTitle("Milestone Details")}
      {renderMilestoneStepper()}
    </div>
  );
};

const PopperContainer = styled(Paper)(({ theme }) => ({
  padding: "16px 24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.1)",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: "-10px",
    left: "50%",
    transform: "translateX(-50%)",
    borderLeft: "10px solid transparent",
    borderRight: "10px solid transparent",
    borderBottom: "10px solid white",
  },
}));

const CertificatesView = () => {
  const { files } = useAnalystJobView();
  const [newFiles, setNewFiles] = useState([]);
  const [newFile, setNewFile] = useState<Record<string, any>>({});
  const [addInward, setAddInward] = useState(false);

  const renderFileList = (title, documents) =>
    documents?.length > 0 && (
      <ol className="list-a column">
        {documents.map((doc, idx) => (
          <li key={idx}>
            <div className="row small">
              <span>{doc.name}</span>
              {doc.url ? (
                <div className="row end">
                  {renderPreviewButtonWithIcon("Preview", "link", { to: doc.url, target: "_blank" })}
                  {renderDeleteButton(() => {})}
                </div>
              ) : (
                renderOutlineButton("Generate", true, "button", {})
              )}
            </div>
          </li>
        ))}
      </ol>
    );

  const generatedOutwardDocuments = files.outwardDocuments;
  const jobDocumentsTitlesList = jobDocumentsTitles.map((title) => title.title);
  const outwardDocumentsList = [
    ...jobDocumentsTitles.map((title) => {
      const generatedDoc = generatedOutwardDocuments.find((doc) => doc.name === title.title);
      return { name: title.title, url: generatedDoc?.url };
    }),
    ...generatedOutwardDocuments
      .filter((doc) => !jobDocumentsTitlesList.includes(doc.name))
      .map((doc) => ({ name: doc.name, url: doc.url })),
  ];

  const generatedFinancialDocuments = files.financialDocuments;
  const financialDocumentsTitlesList = financialDocumentsTitles.map((title) => title.title);
  const financialDocumentsList = [
    ...financialDocumentsTitles.map((title) => {
      const generatedDoc = generatedFinancialDocuments.find((doc) => doc.name === title.title);
      return { name: title.title, url: generatedDoc?.url };
    }),
    ...generatedFinancialDocuments
      .filter((doc) => !financialDocumentsTitlesList.includes(doc.name))
      .map((doc) => ({ name: doc.name, url: doc.url })),
  ];

  return (
    <div className="card rounded column spread">
      {renderSectionTitle("Certificates")}
      <ol className="column">
        <li>
          <div className="column">
            <div className="row">
              <span className="label">Inward Documents</span>
              {renderGhostButton(addInward ? "Cancel" : "+ Add Document", true, "button", {
                onClick: () => {
                  setAddInward((prev) => !prev);
                  setNewFile({});
                },
              })}
            </div>
            {renderFileList("Inward Documents", [...files.inwardDocuments, ...newFiles])}
            {addInward && (
              <div className="row">
                {renderInput("text", "new_inward_document_name", "new_inward_document_name", newFile.name, {
                  onChange: (e) => setNewFile((prev) => ({ ...prev, name: e.target.value })),
                  className: "full",
                })}
                <div className="row end">
                  {renderFileInputButton(
                    newFile.file ? "Re-upload" : "Upload",
                    "new_inward_document",
                    "new_inward_document",
                    newFile.url,
                    {
                      onChange: (e) =>
                        setNewFile((prev) => ({
                          ...prev,
                          file: e.target.files[0],
                          url: URL.createObjectURL(e.target.files[0]),
                        })),
                    }
                  )}
                  {renderOutlineButton("Save", false, "button", {
                    disabled: !newFile.name || !newFile.file,
                    onClick: () => {
                      setNewFiles((prev) => [...prev, newFile]);
                      setAddInward(false);
                      setNewFile({});
                    },
                  })}
                </div>
              </div>
            )}
          </div>
        </li>
        <li>
          <div className="column">
            <span className="label">Outward Documents</span>
            {renderFileList("Outward Documents", outwardDocumentsList)}
          </div>
        </li>
        <li>
          <div className="column">
            <span className="label">Financial Documents</span>
            {renderFileList("Financial Documents", financialDocumentsList)}
          </div>
        </li>
      </ol>
    </div>
  );
};

const DesignDrawingDropdown = ({ setFormData, formData, tasksList, disabled }) => {
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [showTaskAssignModal, setShowTaskAssignModal] = useState(false);

  const assignedTasksNames = useMemo(() => formData.assignedTasksNames ?? [], [formData.assignedTasksNames]);

  const groupedOptions = useMemo(() => {
    const grouped = {};
    tasksList.forEach((task) => {
      const [prefix] = task.split(" ")[0].split(".");
      if (!grouped[prefix]) grouped[prefix] = [];
      grouped[prefix].push(task);
    });
    return Object.keys(grouped)
      .map((prefix) => [
        { label: prefix, isLabel: true },
        ...grouped[prefix].map((task) => ({ label: task, value: task, disabled: assignedTasksNames.includes(task) })),
      ])
      .flat();
  }, [assignedTasksNames, tasksList]);

  const options = useMemo(() => groupedOptions.filter((option) => !option.isLabel), [groupedOptions]);

  const selectedValues = useMemo(
    () =>
      selectedOptions?.length > 0
        ? selectedOptions.map((option) => option.selected && option.value).filter(Boolean)
        : [],
    [selectedOptions]
  );
  const allSelected = useMemo(
    () => selectedValues.filter(Boolean).length === options.length,
    [selectedValues, options.length]
  );

  const handleSelectAll = () => {
    setSelectedOptions(
      allSelected
        ? []
        : options.map(
            (option) => selectedOptions.find((o) => o.value === option.value) ?? { ...option, selected: true }
          )
    );
    setFormData((prev) => ({
      ...prev,
      tasks: allSelected
        ? []
        : options.map((opt) => selectedOptions.find((o) => o.value === opt.value) ?? { ...opt, selected: true }),
    }));
  };

  const handleSelect = (value) => {
    const isSelected = selectedValues.includes(value);
    let selected = isSelected ? selectedValues.filter((v) => v !== value) : [...selectedValues, value];
    const selectedOpts = options
      .filter((option) => selected.includes(option.value))
      .map((option) => ({ ...option, selected: true }));
    setSelectedOptions(selectedOpts);
    setFormData((prev) => ({ ...prev, tasks: selectedOpts.filter((option) => option.selected) }));
  };

  const handleInputChange = (e, label) => {
    const { name, value } = e.target;
    const currentOption = selectedOptions.find((option) => option.label === label);
    currentOption[name] = +value;
    setSelectedOptions((prev) => prev.map((option) => (option.label === label ? currentOption : option)));
    const updatedFormDataTasks = formData.tasks.map((task) =>
      task.label === label ? { ...task, [name]: +value } : task
    );

    const updatedFormDataWorkTimeline = {
      hours: updatedFormDataTasks.reduce((acc, task) => acc + (isNaN((task as any).hours) ? 0 : (task as any).hours), 0),
      minutes: updatedFormDataTasks.reduce((acc, task) => acc + (isNaN((task as any).minutes) ? 0 : (task as any).minutes), 0),
    };
    setFormData((prev) => ({
      ...prev,
      tasks: updatedFormDataTasks,
      work_timeline: updatedFormDataWorkTimeline,
    }));
  };

  useEffect(() => {
    setSelectedOptions(formData.tasks?.length > 0 ? formData.tasks : []);
  }, [formData.tasks]);

  return (
    <>
      <ButtonAsDropdown options={{ onClick: () => setShowTaskAssignModal(true), disabled: disabled }}>
        {selectedValues.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, fontSize: "0.875rem", maxWidth: "23rem" }}>
            {selectedValues.filter(Boolean).map((value) => (
              <Chip key={value} label={value} size="small" sx={{ fontSize: "0.625rem" }} />
            ))}
          </Box>
        ) : (
          "Assign Task"
        )}
      </ButtonAsDropdown>
      <ModalWrapper
        open={showTaskAssignModal}
        onClose={() => setShowTaskAssignModal(false)}
        aria-labelledby="assign-task-modal"
        aria-describedby="assign-task-modal"
      >
        <div className="modal_container full column task-assign-modal">
          <div className="column">
            <div className="small font-md">Select Tasks</div>
            <Box sx={{ paddingInline: "1rem" }}>
              <FormControlLabel
                htmlFor="select-all"
                control={
                  <Checkbox
                    id="select-all"
                    checked={allSelected}
                    color="default"
                    onChange={handleSelectAll}
                    inputProps={{ "aria-label": "Select All" }}
                  />
                }
                label="Select All"
              />
            </Box>
            {groupedOptions.length > 0 && (
              <Box sx={{ display: "grid", gridTemplateColumns: "4rem 1fr 1fr", columnGap: 2 }}>
                {groupedOptions.map((option, index) =>
                  option.isLabel ? (
                    <ListSubheader key={option.label}>{option.label}:</ListSubheader>
                  ) : (
                    <Box
                      key={option.value}
                      sx={{
                        py: "0.25rem",
                        display: "grid",
                        alignItems: "center",
                        gridTemplateColumns: "1fr 1fr",
                        rowGap: 1,
                      }}
                    >
                      <label htmlFor={`option-${index}`} className="label">
                        <Checkbox
                          id={`option-${index}`}
                          checked={option.disabled ? true : selectedValues.includes(option.value)}
                          disabled={option.disabled ? true : false}
                          onChange={() => handleSelect(option.value)}
                          inputProps={{ "aria-label": option.label }}
                        />
                        <span>{option.label}</span>
                      </label>
                      {/* <FormControlLabel
                        control={
                        }
                        label={option.label}
                      /> */}

                      {selectedValues?.length > 0 && selectedValues?.includes(option.value) && (
                        <Box
                          sx={{
                            display: "flex",
                            ml: 1,
                            fontSize: "0.875rem",
                            "& .input": { maxWidth: "3.5rem" },
                            gap: 0.5,
                          }}
                        >
                          <input
                            type="number"
                            name="hours"
                            id="hours"
                            value={selectedOptions.find((o) => o.label === option.label)?.hours}
                            onChange={(e) => handleInputChange(e, option.label)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="input shrink"
                            placeholder="hrs"
                            min={0}
                            max={23}
                          />
                          <span>:</span>
                          <input
                            type="number"
                            name="minutes"
                            id="minutes"
                            value={selectedOptions.find((o) => o.label === option.label)?.minutes}
                            onChange={(e) => handleInputChange(e, option.label)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                            className="input shrink"
                            placeholder="mins"
                            min={0}
                            max={59}
                          />
                        </Box>
                      )}
                    </Box>
                  )
                )}
              </Box>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );
};

const IndividualTaskAssignmentForm = ({ taskName, disabled }) => {
  const {
    commonJobDetails,
    files,
    allTaskList,
    assignedTasksNames,
    drawingEmployees,
    designEmployees,
    assignTaskToEmployee,
  } = useAnalystJobView();
  const { jobCode, jobName } = commonJobDetails;
  const [isActive, setIsActive] = useState(!disabled);
  const [priority, setPriority] = useState("Low");
  const { id: jobId } = useParams();

  const department = taskName.split(" ")[1];

  const initialFormData = useMemo(
    () => ({
      tasks: [{ label: taskName, selected: true, value: taskName }],
      assignedTasksNames,
      documents_shared: [],
      employee_id: null,
      note: "",
    }),
    [taskName, assignedTasksNames]
  );
  const [formData, setFormData] = useState(initialFormData);

  const validateForm = (data) => {
    if (!isNaN(data.jobId)) {
      toast.error("Invalid Job ID");
      return false;
    }

    let errorCount = 0;

    if (!data.department || ["Design", "Drawing"].indexOf(data.department) === -1) {
      toast.error(`Invalid department: ${data.department}`);
      errorCount += 1;
    }

    if (!data.priority || ["Urgent", "High", "Medium", "Low"].indexOf(data.priority) === -1) {
      toast.error(`Invalid priority: ${data.priority}`);
      errorCount += 1;
    }

    if (!data.employee_id) {
      toast.error("Please select an employee to assign the task to.");
      errorCount += 1;
    }

    if (data.tasks.length === 0) {
      toast.error("Please select at least one task to assign.");
      errorCount += 1;
    } else {
      const missingTimes = data.tasks.filter((task) => !(task as any).hours && !(task as any).minutes).map((t) => t.task_name);
      if (missingTimes.length > 0) {
        toast.error(`Please enter hours and minutes for the following tasks: ${missingTimes.join(", ")}`);
        errorCount += 1;
      }

      const invalidTimes = data.tasks
        .filter(
          (task) =>
            missingTimes.indexOf(task.task_name) === -1 &&
            (isNaN((task as any).hours) ||
              isNaN((task as any).minutes) ||
              ((task as any).hours === 0 && (task as any).minutes === 0) ||
              ((task as any).hours < 0 && (task as any).hours > 23) ||
              ((task as any).minutes < 0 && (task as any).minutes > 59))
        )
        .map((t) => t.task_name);
      if (invalidTimes.length > 0) {
        toast.error(`Please enter valid hours and minutes for the following tasks: ${invalidTimes.join(", ")}`);
        errorCount += 1;
      }
    }

    if (errorCount > 0) return false;

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const dataToSave = {
      job_id: +jobId,
      department: department,
      priority: priority,
      employee_id: formData.employee_id,
      documents_shared: formData.documents_shared,
      tasks: formData.tasks.map((task) => ({
        name: task.label,
        assigned_week: formData.scheduled.assigned_week,
        alloted_time: `${(task as any).hours ? `${(task as any).hours}`.padStart(2, '0') : "00"}:${
          (task as any).minutes ? `${(task as any).minutes}`.padStart(2, '0') : "00"
        }`,
        hours: (task as any).hours || 0,
        minutes: (task as any).minutes || 0,
      })),
      note: formData.note,
    };
    if (!validateForm(dataToSave)) return;
    assignTaskToEmployee(dataToSave);
  };

  const tasksList = allTaskList.filter((task) => task.split(" ")[1] === department);

  return (
    <form
      id={taskName ? `assign_${taskName}_form` : "task_assign_form"}
      className={`card rounded column small ${!isActive ? "disabled" : ""}`}
      onSubmit={handleSubmit}
    >
      <div className="row end">
        {!isActive ? renderOutlineButton("Assign", true, "button", { onClick: () => setIsActive(true) }) : null}
      </div>
      <div className="grid_3 grid-with-divider">
        <div className="row">
          <span>Job code</span>
          <span className="font-md">{jobCode}</span>
        </div>
        <div className="row">
          <span>Project Name</span>
          <span className="font-md">{jobName}</span>
        </div>
        <div className="row">
          <span>Employee</span>
          <EmployeeListDropdown
            employees={
              department.toLowerCase() === Departments.DRAWING
                ? drawingEmployees
                : department.toLowerCase() === Departments.DESIGN
                ? designEmployees
                : [...drawingEmployees, ...designEmployees]
            }
            setFormData={setFormData}
            formData={formData}
            disabled={!isActive}
          />
        </div>
      </div>

      <div className="grid-2-cols-with-divider">
        <div className="row align_start">
          <span className="label">Designs/Drawings</span>
          <DesignDrawingDropdown
            setFormData={setFormData}
            formData={formData}
            tasksList={tasksList}
            disabled={!isActive}
          />
        </div>
        <div className="row align_start">
          <span className="label">Inward Documents</span>
          {files.inwardDocuments?.length > 0 ? (
            <CheckboxSelect
              hasSelectAll
              name="inward_documents"
              id="inward_documents"
              placeholder="Select Inward Documents"
              options={files.inwardDocuments.map((item) => ({ value: item.name, label: item.name, ...item }))}
              values={formData.documents_shared ?? []}
              onChange={(value) => setFormData((prev) => ({ ...prev, documents_shared: value }))}
              disabled={!isActive}
              full
            />
          ) : (
            <span className="small gray">No Inward Documents Available</span>
          )}
        </div>
      </div>
      <div className="grid-2-cols-with-divider">
        <div className="row">
          <span>Enter work timeline</span>
          <div className="row end">
            {renderInput("number", "hours", "hours", `${(formData as any).work_timeline?.hours}`.padStart(2, "0"), {
              className: "shrink",
              placeholder: "Hrs",
              min: 0,
              disabled: !isActive,
              readOnly: true,
            })}
            <span>:</span>
            {renderInput("number", "minutes", "minutes", `${(formData as any).work_timeline?.minutes}`.padStart(2, "0"), {
              className: "shrink",
              placeholder: "Mins",
              min: 0,
              max: 59,
              disabled: !isActive,
              readOnly: true,
            })}
          </div>
        </div>
        <div className="row">
          <span>Select Priority</span>
          <div>
            {["Urgent", "High", "Medium", "Low"].map((level) => (
              <button
                key={level}
                type="button"
                className={`priority-btn btn" ${level.toLowerCase()} ${priority === level ? "selected" : ""}`}
                onClick={() => setPriority(level)}
                disabled={!isActive}
              >
                {level}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="label_grid">
        <span>Note/Comment</span>
        {renderTextArea("note", "note", formData.note, {
          onChange: (e) => setFormData((prev) => ({ ...prev, note: e.target.value })),
          placeholder: "Enter text",
          disabled: !isActive,
        })}
      </div>
      <div className="row end">
        {renderOutlineButton("Cancel", false, "button", {
          onClick: () => {
            setIsActive(false);
            setFormData(initialFormData);
          },
          disabled: !isActive,
        })}
        {renderCTAButton("Assign Work", "submit", { disabled: !isActive })}
      </div>
    </form>
  );
};

const AssignedTaskView = ({ taskData }) => {
  const { commonJobDetails } = useAnalystJobView();
  const { jobCode, jobName } = commonJobDetails;
  const [showDetails, setShowDetails] = useState(false);

  return (
    <div className="card rounded column small">
      <div className="row">
        {renderTaskName(taskData.task_name)}
        <div className="row end">
          {renderEditButton(() => {})}
          {renderDeleteButton(() => {})}
          {renderToggleButton(null, showDetails, () => setShowDetails((prev) => !prev))}
        </div>
      </div>
      {showDetails && (
        <>
          {renderProjectDetailsForTask(jobCode, jobName, taskData.employee_name)}
          <div className="grid_3 grid-with-divider start align_start">
            {renderLabelValueRow("Work Timeline", formatTimeString(taskData.alloted_time))}
            {renderLabelValueRow(
              "Priority",
              <span className={`font-md priority_${taskData.priority.toLowerCase()}`}>{taskData.priority}</span>
            )}
            <div className="row align_start">
              <span>Inward Documents</span>
              <div>
                {taskData.inward_documents?.length > 0 ? (
                  taskData.inward_documents.map((doc) => (
                    <div className="row end">
                      {renderGhostButton(doc.name, true, "link", { to: doc.url, target: "_blank" })}
                    </div>
                  ))
                ) : (
                  <span className="small gray">No Inward Documents Available</span>
                )}
              </div>
            </div>
          </div>
          <div className="label_grid">
            <span className="label gray">Note/Comment</span>
            <span className="value">{taskData.note}</span>
          </div>
        </>
      )}
    </div>
  );
};

const TaskSubmissionView = ({ taskData, taskStatus }) => {
  const { commonJobDetails, pocNames } = useAnalystJobView();
  const { jobCode, jobName } = commonJobDetails;

  const [showDetails, setShowDetails] = useState(true);
  const analyst = JSON.parse(localStorage.getItem("userData"))?.name;

  const initialFormData = {
    analyst_name: analyst,
    analyst_comment: "",
    revision_requested: false,
    work_shared: false,
    poc: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  const pocOptions = useMemo(
    () =>
      Object.keys(pocNames).map((key) => ({
        value: key,
        label: (
          <span className="row full">
            <span>{capitalizeString(key)}</span>
            <span className="gray">{pocNames[key]}</span>
          </span>
        ),
      })),
    [pocNames]
  );

  const shareOptions = useMemo(
    () => [
      ...pocOptions,
      {
        value: "front-desk",
        label: (
          <span className="row full">
            <span>Front Desk</span>
            <span className="gray">{receptionistName}</span>
          </span>
        ),
      },
    ],
    [pocOptions]
  );

  const revisionOptions = useMemo(
    () => [
      ...pocOptions,
      {
        value: "analyst",
        label: (
          <span className="row full">
            <span>Analyst</span>
            <span className="gray">{analyst}</span>
          </span>
        ),
      },
    ],
    [analyst, pocOptions]
  );

  const renderShareForm = (data, disabled) =>
    data.work_shared || formData.work_shared ? null : (
      <form id={`share-form-${taskData.id}`} className="column">
        <div className="column">
          <div className="row start">
            <label htmlFor="selected_poc" className="label">
              Point of Communication
            </label>
            <SelectInput
              id="poc"
              options={{
                value: disabled
                  ? shareOptions.find((opt) => opt.value === data.revision_requested_by.toLowerCase())?.label
                  : formData.poc
                  ? shareOptions.find((opt) => opt.value === formData.poc)?.label
                  : null,
                placeholder: "Select",
                onChange: (value) => setFormData((prev) => ({ ...prev, poc: value, poc_name: shareOptions[value] })),
                disabled: disabled,
              }}
              selectMenuOptions={shareOptions}
              isFull
            />
          </div>
          <div className="row">
            <div className="column shrink align_start sm gray">
              {disabled ? (
                data.submissionStatus === "Revision Requested" ? (
                  data.revision_requested_by === "Analyst" ? (
                    <span className="row shrink">
                      <Icon icon="subway:round-arrow-1" className="icon sm" />
                      Revision requested by {data.analystName}
                    </span>
                  ) : (
                    <>
                      <span className="row shrink">
                        <Icon icon="tabler:check" className="icon" /> Sent to{" "}
                        {[pocNames[data.revision_requested_by.toLowerCase()]]}
                      </span>
                      <span className="row shrink">
                        <Icon icon="subway:round-arrow-1" className="icon sm" />
                        Revision requested by {pocNames[data.revision_requested_by.toLowerCase()]}
                      </span>
                    </>
                  )
                ) : data.submissionStatus === "Approved" ? (
                  <>
                    <span className="row shrink">
                      <Icon icon="tabler:check" className="icon" /> Sent to{" "}
                      {[pocNames[data.revision_requested_by.toLowerCase()]]}
                    </span>
                    <span className="row shrink">
                      <Icon icon="ep:success-filled" className="icon-lg text-green" /> Approved by{" "}
                      {data.revision_requested_by === "Analyst"
                        ? data.analystName
                        : pocNames[data.revision_requested_by.toLowerCase()]}
                    </span>
                  </>
                ) : null
              ) : null}
            </div>
            {renderCTAButtonWithIcon("Share file", "majesticons:share-line", null, "button", {
              disabled: disabled,
              onClick: () => setFormData((prev) => ({ ...prev, work_shared: true })),
            })}
          </div>
          {data.revision_request_reason ? (
            <div className="row small align_start">
              <span className="label">Revision Request Reason</span>
              <span className="value text_right">{data.revision_request_reason}</span>
            </div>
          ) : null}
        </div>
        <hr />
        <div className="column">
          <div className="row start">
            <label htmlFor="selected_poc" className="label">
              Revision Requested By
            </label>
            <SelectInput
              id="revision_requested_by"
              options={{
                value: disabled
                  ? shareOptions.find((opt) => opt.value === data.revision_requested_by.toLowerCase())?.label
                  : formData.poc
                  ? shareOptions.find((opt) => opt.value === formData.poc)?.label
                  : null,
                placeholder: "Select",
                onChange: (value) => setFormData((prev) => ({ ...prev, selected_poc: value })),
                disabled: disabled,
              }}
              isFull
              selectMenuOptions={revisionOptions}
            />
          </div>
          {
            <div className="row spread">
              <label htmlFor="analyst_comment" className="label">
                Add comment
              </label>
              {renderInput("text", "analyst_comment", "analyst_comment", formData.analyst_comment, {
                onChange: (e) => setFormData((prev) => ({ ...prev, analyst_comment: e.target.value })),
                className: "full",
                disabled: disabled,
              })}
            </div>
          }

          <div className="row end">
            {renderOutlineButton("Revise", true, "button", {
              onClick: () => setFormData((prev) => ({ ...prev, revision_requested: true })),
              disabled: disabled,
            })}
          </div>
        </div>
      </form>
    );

  const renderRevisionCard = (data, disableForm) => (
    <div className="card with-bg column small">
      <div className="row">
        {renderTaskName(taskData.task_name)}
        {data.file_url ? (
          <>
            <span className="gray small">Uploaded on {formatDate(data.submitted_date)}</span>
            {renderGhostButton("preview", true, "link", { to: data.file_url, target: "_blank" })}
          </>
        ) : (
          <span className="gray small">No file uploaded</span>
        )}
      </div>
      <div className="label_grid">
        <span className="label">{taskData.employee_name}:</span>
        <span className="value">{data.note}</span>
      </div>
      {renderShareForm(data, disableForm)}
      {/* {renderRequestedDetails(data, disableForm)} */}
    </div>
  );

  return (
    <div className="card rounded column small">
      <div className="row">
        {renderTaskName(taskData.task_name)}
        <div className="row end">
          {renderEditButton(() => {})}
          {renderDeleteButton(() => {})}
          {renderToggleButton(null, showDetails, () => setShowDetails((prev) => !prev))}
        </div>
      </div>
      <div className="grid_3 grid-with-divider">
        <div className="row">
          <span>Job code</span>
          <span className="font-md">{jobCode}</span>
        </div>
        <div className="row">
          <span>Project Name</span>
          <span className="font-md">{jobName}</span>
        </div>
        <div className="row">
          <span>Employee</span>
          <span className="font-md">{taskData.employee_name}</span>
        </div>
      </div>
      <div className="grid_3 grid-with-divider">
        <div className="column shrink">
          <div className="row align_start">
            <span>Work Timeline</span>
            <span className="font-md">{formatTimeString(taskData.alloted_time)}</span>
          </div>
          {/* if submitted */}
          {["Approved", "Revision_Requested", "Submitted"].includes(taskData.status) && (
            <div className="row align_start">
              <span>Time used</span>
              <span className={getTimeUsedClass(taskData.alloted_time, taskData.time_used)}>
                {formatTimeString(taskData.time_used)}
              </span>
            </div>
          )}
        </div>
        <div className="row align_start">
          <span>Priority</span>
          <span className={`font-md priority_${taskData.priority.toLowerCase()}`}>{taskData.priority}</span>
        </div>
        <div className="row align_start">
          <span>Inward Documents</span>
          <div>
            {taskData.documents_shared?.length > 0 ? (
              taskData.documents_shared.map((doc) => (
                <div className="row end">
                  {renderGhostButton(doc.name, true, "link", { to: doc.url, target: "_blank" })}
                </div>
              ))
            ) : (
              <span className="small gray">No Inward Documents Available</span>
            )}
          </div>
        </div>
      </div>
      <div className="label_grid_full">
        <span className="label">Note/Comment</span>
        <span className="value">{taskData.note}</span>
      </div>
      {showDetails && (
        <div className="grid-2-cols">
          {taskData.submissions.map((data, index) => (
            <div className="column shrink" key={index}>
              <span className="font-md small">R{data.revision_number - 1}</span>
              {renderRevisionCard(
                data,
                data.revision_number < taskData.submissions.length || taskStatus === "Approved"
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WorkAssignment = () => {
  const { allTaskList, assignedTasks } = useAnalystJobView();
  const assignedTasksNames = assignedTasks.map((task) => task.task_name);
  const [isSectionVisible, setIsSectionVisible] = useState(true);

  return (
    <div className="column card">
      <div className="row">
        {renderSectionTitle("Work Summary")}
        {renderToggleButton(null, isSectionVisible, () => setIsSectionVisible((prev) => !prev))}
      </div>
      {isSectionVisible &&
        allTaskList.length > 0 &&
        allTaskList.map((task, index) =>
          assignedTasksNames.includes(task) ? (
            <AssignedTaskView key={index} taskData={assignedTasks.find((t) => t.task_name === task)} />
          ) : (
            <IndividualTaskAssignmentForm key={index} taskName={task} disabled={true} />
          )
        )}
    </div>
  );
};

const WorkInProgress = () => {
  const { tasksInProgress } = useAnalystJobView();

  return (
    tasksInProgress.length > 0 &&
    tasksInProgress.map((task, index) => <TaskSubmissionView key={index} taskData={task} taskStatus="InProgress" />)
  );
};

const WorkApproval = () => {
  const { approvedTasks } = useAnalystJobView();

  return approvedTasks.length > 0 ? (
    approvedTasks.map((task, index) => <TaskSubmissionView key={index} taskData={task} taskStatus="Approved" />)
  ) : (
    <span className="gray small">No tasks approved</span>
  );
};

const WorkProgressView = () => {
  const { workProgressSteps, handleWorkProgressStepChange, currentWorkStep } = useAnalystJobView();

  const renderStepper = () => (
    <Stepper nonLinear alternativeLabel>
      {workProgressSteps.map((step, index) => (
        <Step
          key={step.label}
          active={step.status === "active" || index === currentWorkStep.index}
          completed={step.status === "completed"}
          onClick={() => {
            if (step.disabled) return;
            handleWorkProgressStepChange(index);
          }}
          disabled={step.disabled}
        >
          <StepButton
            icon={
              <StepperLabelIcon
                active={step.status === "active" || index === currentWorkStep.index}
                completed={step.status === "completed"}
              />
            }
          >
            {step.label}
          </StepButton>
        </Step>
      ))}
    </Stepper>
  );

  const stepMap = {
    assign: <WorkAssignment />,
    progress: <WorkInProgress />,
    approval: <WorkApproval />,
  };

  return (
    <>
      {renderStepper()}
      <JobDetails hide={true} />
      {stepMap[currentWorkStep.handle] || "Error: not found"}
    </>
  );
};

const AssignedSiteVisit = ({ data }) => {
  const { maxFreeVisitCount, siteVisitDetails } = useAnalystJobView();
  const { jobCode, jobName, siteAddress } = siteVisitDetails;

  const [isSectionVisible, setIsSectionVisible] = useState(true);
  const handleSectionToggle = () => {
    setIsSectionVisible((prev) => !prev);
  };
  const [work, setWork] = useState(data);

  const inwardDocuments = useMemo(
    () => work?.documents_shared?.filter((doc) => doc?.category === "inward_documents"),
    [work]
  );
  const outwardDocuments = useMemo(
    () => work?.documents_shared?.filter((doc) => doc?.category === "outward_documents"),
    [work]
  );

  useEffect(() => {
    setWork(data);
  }, [data]);

  return (
    <div className="card rounded small column">
      <div className="row">
        <div>
          <span>
            Work: Site Visit{" "}
            <span className="gray">
              ({work.is_free_visit && `${work.visit_count}/${maxFreeVisitCount} `}
              {work.is_free_visit ? "Free Visit" : "Paid Visit"})
            </span>
          </span>
        </div>
        <div className="row end">
          <button className="btn ghost_btn no_underline">
            Edit <Icon icon="carbon:edit" />
          </button>
          <button className="btn ghost_btn no_underline">
            Delete <Icon icon="ic:outline-delete" />
          </button>
          <button onClick={handleSectionToggle} className="btn toggle_btn">
            <Icon icon="mingcute:down-line" className={`text-lg toggle_icon ${isSectionVisible ? "" : "hide"}`} />
          </button>
        </div>
      </div>
      {isSectionVisible && (
        <>
          <div className="grid_3 grid-with-divider">
            <div className="row">
              <span className="label">Job code</span>
              <span className="value font-md">{jobCode}</span>
            </div>
            <div className="row">
              <span className="label">Project name</span>
              <span className="value font-md">{jobName}</span>
            </div>
            <div className="row">
              <span className="label">Employee</span>
              <span className="value font-md">{work?.site_engineer_name}</span>
            </div>
          </div>
          <div className="grid-2-cols-with-divider">
            <div className="row align_start">
              <span className="label">Site Address</span>
              <span className="value">{siteAddress}</span>
            </div>
            <div className="column shrink">
              <div className="row align_start">
                <span className="label">Date</span>
                <span className="value">{formatDate(work?.assigned_date)}</span>
              </div>
              <div className="row">
                <span className="label">Time</span>
                <span className="gray">{data.status}</span>
                <span className="value">{data.assigned_time}</span>
              </div>
            </div>
          </div>
          <div className="grid-2-cols-with-divider">
            <div className="row align_start full">
              <span className="label">Inward Documents</span>
              <div className="column shrink">
                {inwardDocuments?.length > 0 ? (
                  inwardDocuments?.map((doc, index) => (
                    <Link to={doc.url} className="cta_ghost_btn row end" key={index}>
                      <span className="value">{doc.name}</span>
                    </Link>
                  ))
                ) : (
                  <span className="value gray small text_right">No document</span>
                )}
              </div>
            </div>
            <div className="row align_start full">
              <span className="label">Outward Documents</span>
              <div className="column shrink">
                {outwardDocuments?.map((doc, index) => (
                  <Link to={doc.url} className="cta_ghost_btn row end" key={index}>
                    <span className="value">{doc.name}</span>
                  </Link>
                )) || <span className="value gray small text_right">No document</span>}
              </div>
            </div>
          </div>
          <div className="row align_start">
            <span className="label">Note</span>
            <span className="value">{work?.note}</span>
          </div>
          {work?.status.toLowerCase() === "completed" && <SiteVisitReport data={work} />}
        </>
      )}
    </div>
  );
};

const SiteVisitReport = ({ data }) => {
  const { commonJobDetails, basicDetails } = useAnalystJobView();
  const renderRow = (label, value) => (
    <div className="row align_start small">
      <span className="label gray">{label}</span>
      <span className="value">{value}</span>
    </div>
  );

  return (
    <>
      <div className="column">
        <div className="row">
          <span className="font-md">Site Visit Report</span>
          <button type="button" className="btn cta_outline_btn">
            <Icon icon="mynaui:download" />
            Download site visit report
          </button>
        </div>
        <div className="grid-2-cols-with-divider">
          <div className="column">
            {renderRow("Site visit made along with", data?.employee_name)}
            {renderRow("Date", formatDate(data?.assigned_date))}
            {renderRow("Time", "15:30")}
            {renderRow("Job no.", commonJobDetails?.jobCode)}
            {renderRow("Drg. no.", data?.drawing_number)}
            {renderRow("Revision no.", data?.revision_number)}
            {renderRow("Project name", commonJobDetails?.jobName)}
            {renderRow("Location", basicDetails?.locality)}
          </div>
          <div className="column shrink">
            <span className="label gray">Report Summary</span>
            <ol className="card rounded column shrink">
              {data?.report_summary.split(". ").map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ol>
          </div>
        </div>
      </div>
      <div className="card rounded small column">
        <span className="font-md">Site Visit Images</span>
        <div className="grid-2-cols spread">
          {data?.images?.map((img, index) => (
            <div className="row align_start shrink">
              <img src={img.url} alt="" key={index} className="site_visit_image" />
              <div className="column full between">
                <span>{img.comment}</span>
                <div className="row end">
                  {renderGhostButtonWithIcon("Download", "mynaui:download", null, false, "button", {
                    className: "no_underline",
                  })}
                  {renderPreviewButtonWithIcon("View", "link", { className: "no_underline", to: img.url })}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="label_grid_full">
        <span className="label">Analyst Note</span>
        <span className="value">{data?.analyst_comment}</span>
      </div>
    </>
  );
};

const SiteVisitView = () => {
  const { assignedSiteVisits, assignNewSiteVisit } = useAnalystJobView();
  const isFormVisible = useMemo(() => assignNewSiteVisit, [assignNewSiteVisit]);
  const [showForm, setShowForm] = useState(false);

  const scheduledSiteVisits = useMemo(
    () => assignedSiteVisits.filter((visit) => visit.status.toLowerCase() !== "completed"),
    [assignedSiteVisits]
  );

  useEffect(() => {
    setShowForm(scheduledSiteVisits.length === 0 || isFormVisible);
  }, [isFormVisible, scheduledSiteVisits.length]);

  return (
    <>
      {showForm && <SiteVisitForm />}
      {assignedSiteVisits.length > 0
        ? assignedSiteVisits.map((visit, index) => <AssignedSiteVisit key={index} data={visit} />)
        : null}
    </>
  );
};

const SiteVisitForm = () => {
  const { siteVisitDetails } = useAnalystJobView();
  const {
    jobCode,
    jobName,
    siteAddress,
    employees,
    supervisorName,
    supervisorContact,
    inwardDocuments,
    outwardDocuments,
  } = siteVisitDetails;

  const initialFormData = {};
  const [formData, setFormData] = useState(initialFormData);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form data submitted:", formData);
  };

  const analystOption = { label: "Analyst", value: "Analyst", formValue: "analyst" };
  const directorOption = { label: "Director", value: "Director", formValue: "director" };

  const employeeOptions = [
    analystOption,
    directorOption,
    ...employees.map((emp) => ({ label: emp.name, value: emp.name, formValue: emp.employee_id })),
  ];

  const handleEmployeeSelect = (selectedOptions) => {
    const employeesSelected = selectedOptions.filter(
      (opt) => ![analystOption.value, directorOption.value].includes(opt)
    );
    const selectedEmployee = employeesSelected.length > 0 ? employeesSelected[employeesSelected.length - 1] : null;
    const isAnalystSelected = selectedOptions.includes(analystOption.value);
    const isDirectorSelected = selectedOptions.includes(directorOption.value);
    let selectedEmployeeOptions = [];
    if (isAnalystSelected) selectedEmployeeOptions.push(analystOption.value);
    if (isDirectorSelected) selectedEmployeeOptions.push(directorOption.value);
    if (selectedEmployee) selectedEmployeeOptions.push(selectedEmployee);

    setFormData((prev) => ({
      ...prev,
      selectedEmployeeOptions: selectedEmployeeOptions,
      director_visit: isDirectorSelected,
      analyst_visit: isAnalystSelected,
    }));
  };
  const handleInwardDocumentsSelect = (selectedOptions) => {
    setFormData((prev) => ({ ...prev, inwardDocuments: selectedOptions }));
  };
  const handleOutwardDocumentsSelect = (selectedOptions) => {
    setFormData((prev) => ({ ...prev, outwardDocuments: selectedOptions }));
  };

  const handleCancel = () => {
    setFormData(initialFormData);
  };

  return (
    <form id="site_visit_form" onSubmit={handleSubmit} className="card rounded column small">
      <div className="grid-2-cols-with-divider">
        <div className="row">
          <span>Job code</span>
          <span className="font-md">{jobCode}</span>
        </div>
        <div className="row">
          <span>Project Name</span>
          <span className="font-md">{jobName}</span>
        </div>
      </div>
      <div className="grid-2-cols-with-divider">
        <div className="row">
          <span className="label">Department</span>
          <span className="value">Site Visit</span>
        </div>
        <div className="row">
          <span className="label">Employee</span>
          <CheckboxSelect
            name="site-visit-employee"
            id="site-visit-employee"
            options={employeeOptions}
            placeholder="Select Employee"
            values={(formData as any).selectedEmployeeOptions}
            required
            full
            onChange={handleEmployeeSelect}
          />
        </div>
      </div>
      <div className="grid-2-cols-with-divider">
        <div className="row align_start">
          <span className="label">Site address</span>
          <span className="value">{siteAddress}</span>
        </div>
        <div className="column shrink">
          <div className="row">
            <span className="label">Date</span>
            <input
              type="date"
              name="site-visit-date"
              id="site-visit-date"
              required
              className="input half"
              min={new Date().toISOString().split("T")[0]}
            />
          </div>
          <div className="row">
            <span className="label">Time</span>
            <SelectInput
              id="site-visit-time"
              options={{
                value: formData.time,
                onChange: (value) => setFormData((prev) => ({ ...prev, time: value })),
                placeholder: "Select",
              }}
              selectMenuOptions={[
                { label: "First Half", value: "First Half" },
                { label: "Second Half", value: "Second Half" },
              ]}
            />
          </div>
        </div>
      </div>
      <div className="grid-2-cols-with-divider">
        <div className="row">
          <span className="label">Supervisor Name</span>
          <span className="value">{supervisorName}</span>
        </div>
        <div className="row">
          <span className="label">Supervisor Contact</span>
          <span className="value">{supervisorContact}</span>
        </div>
      </div>
      <div className="grid-2-cols-with-divider">
        <div className="row align_start">
          <span className="label">Inward Documents</span>
          {inwardDocuments.length > 0 ? (
            <CheckboxSelect
              name="inward-documents"
              id="inward-documents"
              options={inwardDocuments.map((item) => ({ value: item.name, label: item.name, ...item }))}
              placeholder="Select Inward Documents"
              required
              onChange={handleInwardDocumentsSelect}
            />
          ) : (
            <span className="gray small">No Inward Documents</span>
          )}
        </div>
        <div className="row align_start">
          <span className="label">Outward Documents</span>
          {outwardDocuments.length > 0 ? (
            <CheckboxSelect
              name="outward-documents"
              id="outward-documents"
              options={outwardDocuments.map((item) => ({ value: item.name, label: item.name, ...item }))}
              placeholder="Select Outward Documents"
              required
              onChange={handleOutwardDocumentsSelect}
            />
          ) : (
            <span className="gray small">No Outward Documents</span>
          )}
        </div>
      </div>
      <div className="row start">
        <span className="label">Note/Comment</span>
        <Input
          type="text"
          name="note"
          id="note"
          value={formData.note}
          options={{
            placeholder: "Enter Note/Comment",
            onChange: (e) => setFormData((prev) => ({ ...prev, note: e.target.value })),
            required: true,
            className: "full",
          }}
        />
      </div>
      <div className="row end">
        {renderOutlineButton("Cancel", false, "button", {
          onClick: handleCancel,
        })}
        {renderCTAButton("Assign Site Visit", "submit", { form: "site_visit_form" })}
      </div>
    </form>
  );
};

const JobViewStepContent = () => {
  const { currentStep } = useAnalystJobView();

  const stepMap = {
    new_job: <JobDetailsView />,
    milestones: <MilestoneView />,
    work_progress: <WorkProgressView />,
    site_visit: <SiteVisitView />,
    certificates: <CertificatesView />,
    completed: "Completed",
  };
  return stepMap[currentStep.handle] || "Error: not found";
};

const JobViewContent = () => {
  const { loadStatus, jobViewSteps, handleStepChange, currentStep, milestoneCount, milestonesCompleted } =
    useAnalystJobView();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStep, setSelectedStep] = useState(null);
  const [params] = useSearchParams();
  const [stepToggles, setStepToggles] = useState({});
  const open = Boolean(anchorEl);

  const handleStepClick = (event, step) => {
    const newAnchorEl = anchorEl === event.currentTarget ? null : event.currentTarget;
    setAnchorEl(newAnchorEl);
    setSelectedStep(step);
  };

  const handleToggleChange = (event, step) => {
    setStepToggles((prev) => ({ ...prev, [step.handle]: event.target.checked }));
  };

  const renderJobViewStepper = () => (
    <Stepper nonLinear alternativeLabel>
      {jobViewSteps.map((step, index) => (
        <Step
          key={step.label}
          active={step.status === "active" || index === currentStep.index}
          completed={step.status === "completed"}
          disabled={step.disabled}
          sx={{ position: "relative" }}
        >
          <StepButton
            icon={
              <StepperLabelIcon
                active={step.status === "active" || index === currentStep.index}
                completed={step.status === "completed"}
              />
            }
            onClick={(e) => {
              if (step.disabled) return;
              if (["site_visit", "certificates"].includes(step.handle) && index === currentStep.index)
                handleStepClick(e, step);
              else {
                setAnchorEl(null);
                handleStepChange(index);
              }
            }}
          >
            {step.label}
            {index === jobViewSteps.findIndex((step) => step.handle === "milestones") && (
              <span className="small">
                <br />
                {milestonesCompleted ?? 0}/{milestoneCount}
              </span>
            )}
          </StepButton>
          {["site_visit", "certificates"].includes(step.handle) && (
            <Popper open={open} anchorEl={anchorEl} placement="bottom" disablePortal style={{ zIndex: 1300 }}>
              <PopperContainer>
                <span>Complete {selectedStep?.label}</span>
                <Switch
                  checked={selectedStep ? stepToggles[selectedStep.handle] || false : false}
                  onChange={(e) => selectedStep && handleToggleChange(e, selectedStep)}
                  color={selectedStep && stepToggles[selectedStep.handle] ? "primary" : "default"}
                />
              </PopperContainer>
            </Popper>
          )}
        </Step>
      ))}
    </Stepper>
  );

  return loadStatus.loading ? (
    <LoadingState height={120} />
  ) : (
    <>
      {renderJobViewStepper()}
      <JobViewStepContent />
    </>
  );
};

function AnalystJobView() {
  return (
    <>
      <CommonJobDetails />
      <JobViewContent />
    </>
  );
}

export default function AnalystJobViewWrapper() {
  return (
    <AnalystJobViewProvider>
      <AnalystJobView />
    </AnalystJobViewProvider>
  );
}
